import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@app/environment';
import {
  APIAdminList,
  ApiCompany,
  ApiCompanyComment,
  ApiCompanyListItem,
  APICountry,
  APIDownloadList,
  APIList,
  ApiOverwrite,
  ApiSector,
  APIUserPageVisits,
  Company,
  companyColumnMap,
  companyColumnUnMap,
  CompanyDetail,
  CompanyReviewScore,
  CompanyShortInfo,
  isCondition,
  ListType,
  mapFilterColumns,
  MenuList,
  OverwriteConfig,
  OverwriteReference,
  prepareCountries,
  SubSector,
  toAdminList,
  toCompanyDetail,
  toCompanyListItem,
  toList,
  toOverwriteDataset,
  toSector,
  User,
} from '@app/model';

import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  finalize,
  ignoreElements,
  map,
  mergeMap,
  retry,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { AuthService } from '../auth';
import {
  buildFetchListOfCompanyRequest,
  COMPANY_LIST_PAGE_SIZE,
  CompanyListLoadOptions,
  CompanyListSource,
  LazyPage,
  PageCache,
  AutocompletePage,
  searchAutocomplete,
  toCompanyListError,
} from '../data';
import { logger } from '@app/shared';
import { RecentList, RecentListsService } from './recent-lists.service';

/** Companies with a name, named by the legal name if needed, sorted by name. */
/**
 * The suggestions in the order of the API, which puts the companies whose
 * name starts with the search text first; sorted by name here, "Spry Fox"
 * came before "Spryfox".
 */
export const toCompanySuggestions = (result: CompanyShortInfo[]) =>
  result
    .filter((x) => !!x.name || !!x.legal_name)
    .map((x) => ({ ...x, name: x.name || (x.legal_name as string) }));

/**
 * The companies and lists opened last, newest first, at most ten: companies
 * by their visits on the server, lists by the history of the browser.
 */
export const recentItems = (
  companies: APIUserPageVisits[],
  recentLists: RecentList[],
  lists: ListType[]
) => {
  const byId = new Map(lists.map((list) => [list.id, list]));
  return [
    ...companies.map((x) => ({
      id: x.companies.business_id,
      name: x.companies.name,
      type: 'company',
      at: new Date(x.date).getTime() || 0,
      routerLink: `/companies/${encodeURIComponent(
        x.companies.business_id
      )}/overview`,
    })),
    ...recentLists
      .filter((x) => byId.has(x.id))
      .map((x) => {
        const list = byId.get(x.id) as ListType;
        return {
          id: 'list-' + list.id,
          name: list.name,
          type: list.type,
          at: x.at,
          routerLink: `/lists/${list.id}`,
        };
      }),
  ]
    .sort((a, b) => b.at - a.at)
    .slice(0, 10);
};

@Injectable({ providedIn: 'root' })
export class CompanyService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private recentLists = inject(RecentListsService);

  private refreshLists = new Subject<boolean>();
  private refreshRecent = new Subject<void>();
  private refreshCompany = new Subject<string>();
  private selectedCompanyId = new ReplaySubject<string>(1);
  private selectedListId = new ReplaySubject<number | null>(1);
  private companyLoading = new ReplaySubject<boolean>(1);
  private companyListLoading = new BehaviorSubject<boolean>(false);
  private lists = new ReplaySubject<ListType[]>(1);
  private preconfiguredLists = new ReplaySubject<ListType[]>(1);

  private companyListSource: CompanyListSource<Company> | null = null;
  /**
   * The loaded pages of the company tables (lists and search), dropped when
   * a request here changes what the tables show.
   */
  public readonly companyPages = new PageCache<LazyPage<Company>>();
  private companyListId: number | null = null;
  private companyListLoadingSubscription: Subscription | null = null;

  // The company pages select the company and subscribe to company$ while the
  // container is checked, which sets the loading state in the middle of that
  // check; the value goes out in the next tick, like companyListLoading$.
  public readonly companyLoading$ = this.companyLoading.pipe(
    distinctUntilChanged(),
    delay(0)
  );

  public readonly companyListLoading$ = this.companyListLoading.pipe(
    distinctUntilChanged(),
    delay(0) // force value change to next tick to prevent change detection error
  );

  public readonly company$: Observable<CompanyDetail> = merge(
    this.refreshCompany,
    this.selectedCompanyId.pipe(distinctUntilChanged())
  ).pipe(
    tap(() => this.companyLoading.next(true)),
    switchMap((companyId) =>
      this.http
        .post<ApiCompany>(`${environment.apiBase}/fetchCompany`, {
          method: 'fetchCompany',
          data: {
            companyId,
          },
        })
        .pipe(
          map((result) => toCompanyDetail(result)),
          finalize(() => {
            this.refreshRecent.next();
            this.companyLoading.next(false);
          })
        )
    ),
    shareReplay(1)
  );

  public readonly lastUpdate$ = this.http
    .post<string>(`${environment.apiBase}/fetchLatestDataLoad`, {
      method: 'fetchLatestDataLoad',
    })
    .pipe(shareReplay(1));

  public readonly sectors$ = this.http
    .post<ApiSector[]>(`${environment.apiBase}/fetchSector`, {
      method: 'fetchSector',
    })
    .pipe(
      map((sectors) => sectors.map(toSector)),
      shareReplay(1)
    );

  public readonly subsectors$ = this.sectors$.pipe(
    map((sectors) =>
      sectors.reduce((acc, cur) => [...acc, ...cur.items], [] as SubSector[])
    )
  );

  public readonly countryCodes$ = this.http
    .post<APICountry[]>(`${environment.apiBase}/fetchCountryCode`, {
      method: 'fetchCountryCode',
    })
    .pipe(map(prepareCountries), shareReplay(1));

  public readonly lists$ = this.lists.pipe(
    map((list) =>
      list.sort((a, b) =>
        a.name.toUpperCase() > b.name.toUpperCase()
          ? 1
          : b.name.toUpperCase() > a.name.toUpperCase()
            ? -1
            : 0
      )
    )
  );

  public readonly preconfiguredLists$ = this.preconfiguredLists.pipe(
    map((list) =>
      list.sort((a, b) =>
        a.name.toUpperCase() > b.name.toUpperCase()
          ? 1
          : b.name.toUpperCase() > a.name.toUpperCase()
            ? -1
            : 0
      )
    )
  );

  public readonly allLists$ = combineLatest([
    this.lists$,
    this.preconfiguredLists$,
  ]).pipe(
    map(([lists, preconfiguredLists]) => [...lists, ...preconfiguredLists]),
    shareReplay(1)
  );

  public selectedList$ = this.selectedListId.pipe(
    switchMap((listId) =>
      this.allLists$.pipe(
        map(
          (lists) =>
            (listId && lists.find((list) => list.id === listId)) || null
        )
      )
    ),
    shareReplay(1)
  );

  public readonly favoriteLists$ = this.lists$.pipe(
    map((lists) => lists.filter((list) => list.is_favorite)),
    shareReplay(1)
  );

  public readonly manualLists$ = this.lists$.pipe(
    map((lists) => lists.filter((list) => list.type === 'manual')),
    shareReplay(1)
  );

  private readonly recent$ = merge(
    this.getRecentCompanies(),
    this.refreshRecent.pipe(switchMap(() => this.getRecentCompanies()))
  ).pipe(shareReplay(1));

  public readonly sideMenu$: Observable<MenuList[]> = combineLatest([
    this.favoriteLists$,
    this.recent$,
    this.allLists$,
    this.recentLists.recent$,
  ]).pipe(
    map(([favoriteLists, recentCompanies, allLists, recentLists]) => [
      {
        id: 1,
        icon: 'fa-regular fa-star',
        name: 'common.nav.favourites',
        emptyText: 'common.nav.noFavourites',
        limit: 7,
        more: {
          label: 'common.nav.allFavourites',
          link: '/lists',
          queryParams: { tab: 'favourites' },
        },
        list: favoriteLists.map((list) => ({
          ...list,
          routerLink: `/lists/${list.id}`,
        })),
      },
      {
        id: 2,
        icon: 'fa-regular fa-clock',
        name: 'common.nav.recent',
        emptyText: 'common.nav.noRecent',
        limit: 5,
        list: recentItems(recentCompanies, recentLists, allLists),
      },
    ]),
    shareReplay(1)
  );

  constructor() {
    merge(this.sectors$, this.countryCodes$).pipe(ignoreElements()).subscribe();
    merge(this.auth.isAuthenticated$, this.refreshLists)
      .pipe(
        switchMap((reload) =>
          reload
            ? this.http
                .post<
                  [APIList[], { list_id: number; lists: APIList['lists'] }[]]
                >(`${environment.apiBase}/fetchListOfCompanyByUser`, {
                  method: 'fetchListOfCompanyByUser',
                  data: { favoritesOnly: false },
                })
                .pipe(
                  map(([lists, preconfiguredLists]) => [
                    lists.map(toList),
                    preconfiguredLists.map((list) =>
                      toList({
                        ...list,
                        is_preconfigured: true,
                        id: list.list_id,
                        user_id: null,
                        config_json: null,
                        is_favorite: null,
                        has_alerts: null,
                      })
                    ),
                  ])
                )
            : of([[] as ListType[], [] as ListType[]])
        ),
        catchError((e) => {
          logger.error(e);
          return of([[] as ListType[], [] as ListType[]]);
        }),
        map(([lists, preconfiguredLists]) => {
          const mapFn = (list: ListType) =>
            list.type === 'dynamic' && list.filter instanceof Array
              ? {
                  ...list,
                  filter: mapFilterColumns(
                    (list.filter.length === 1
                      ? list.filter[0]
                      : list.filter) as unknown[],
                    companyColumnUnMap
                  ),
                }
              : list;
          return [lists.map(mapFn), preconfiguredLists.map(mapFn)];
        })
      )
      .subscribe(([lists, preconfiguredLists]) => {
        this.lists.next(lists);
        this.preconfiguredLists.next(preconfiguredLists);
      });
  }

  public downloadList(listId: number) {
    // this.companyListLoading.next(true);
    const pollExport = (
      input: APIDownloadList
    ): Observable<{ url: string; filename: string }> =>
      this.http
        .post<APIDownloadList>(`${environment.apiBase}/download`, {
          method: 'pollExportStatus',
          data: { exportIdentifier: input.exportIdentifier },
        })
        .pipe(
          mergeMap((result) =>
            input.isComplete
              ? of({
                  url: result.presignedURL || '',
                  filename: result.filename || '',
                })
              : timer(2000).pipe(mergeMap(() => pollExport(result)))
          )
        );

    return this.http
      .post<APIDownloadList>(`${environment.apiBase}/download`, {
        method: 'downloadList',
        data: { listId },
      })
      .pipe(
        mergeMap((result) => pollExport(result)),
        mergeMap(({ url, filename }) =>
          this.http.get(url, { responseType: 'blob' }).pipe(
            map((blob) => ({
              blob,
              filename,
            }))
          )
        )
        // finalize(() => this.companyListLoading.next(false))
      );
  }

  private getRecentCompanies() {
    return this.http
      .post<APIUserPageVisits[]>(`${environment.apiBase}/userPageVisit`, {
        method: 'userPageVisit',
        data: {
          requestType: 'read',
        },
      })
      .pipe(catchError(() => of([])));
  }

  /**
   * Drops the cached company pages once the request answered, before its
   * subscriber reloads a table, and also when it failed half way.
   */
  private changesCompanies<T>() {
    const drop = () => this.companyPages.clear();
    return tap<T>({ next: drop, error: drop });
  }

  /** The source the list page of this list left behind, if any. */
  public companyListSourceOf(listId: number) {
    return listId === this.companyListId ? this.companyListSource : null;
  }

  public setCompanyLoading(loading: boolean) {
    this.companyLoading.next(loading);
  }

  public selectList(listId: number | null) {
    this.selectedListId.next(listId);
  }

  public selectCompany(companyId: string) {
    this.selectedCompanyId.next(companyId);
  }

  public reloadCompany(companyId: string) {
    this.refreshCompany.next(companyId);
  }

  public createCompanyList(
    name: string,
    type: 'manual' | 'dynamic',
    description: string,
    isFavorite = false,
    hasAlerts = false,
    filter?: unknown[] | null
  ) {
    const query =
      !!filter &&
      JSON.stringify(
        isCondition(filter)
          ? [mapFilterColumns(filter, companyColumnMap)]
          : mapFilterColumns(filter, companyColumnMap)
      );
    return this.http
      .post<APIList>(`${environment.apiBase}/insertList`, {
        method: 'insertList',
        data: {
          name,
          type,
          description,
          isFavorite,
          hasAlerts,
          ...(query ? { query } : {}),
        },
      })
      .pipe(finalize(() => this.refreshLists.next(true)));
  }

  public addCompanyToList(companyId: string[], listId: number) {
    return this.http
      .post<unknown>(`${environment.apiBase}/companyList`, {
        method: 'companyList',
        data: {
          requestType: 'create',
          companyId,
          listId,
        },
      })
      .pipe(this.changesCompanies());
  }

  public addCompanyListToList(filter: unknown[] | null, listId: number) {
    return this.http
      .post<unknown>(`${environment.apiBase}/assignDynamicListToManualList`, {
        method: 'assignDynamicListToManualList',
        data: {
          filter:
            filter instanceof Array && filter[0] instanceof Array
              ? filter
              : [filter],
          listId,
        },
      })
      .pipe(this.changesCompanies());
  }

  public removeCompanyFromList(companyId: string[], listId: number) {
    return this.http
      .post<unknown>(`${environment.apiBase}/companyList`, {
        method: 'companyList',
        data: {
          requestType: 'delete',
          companyId,
          listId,
        },
      })
      .pipe(this.changesCompanies());
  }

  public deleteList(listId: number) {
    return this.http
      .post<unknown>(`${environment.apiBase}/modifyList`, {
        method: 'modifyList',
        data: {
          requestType: 'delete',
          listId,
        },
      })
      .pipe(
        this.changesCompanies(),
        finalize(() => this.refreshLists.next(true))
      );
  }

  public updateList(
    listId: number,
    {
      ownerId,
      name,
      description,
      filter,
    }: {
      ownerId?: string;
      name?: string;
      description?: string;
      filter?: unknown[] | null;
    }
  ) {
    const query =
      !!filter &&
      JSON.stringify(
        isCondition(filter)
          ? [mapFilterColumns(filter, companyColumnMap)]
          : mapFilterColumns(filter, companyColumnMap)
      );

    return this.http
      .post<unknown>(`${environment.apiBase}/modifyList`, {
        method: 'modifyList',
        data: {
          requestType: 'update',
          listId,
          ...(ownerId ? { ownerId } : {}),
          ...(name ? { name } : {}),
          ...(description ? { description } : {}),
          ...(query ? { query } : {}),
        },
      })
      .pipe(
        this.changesCompanies(),
        finalize(() => this.refreshLists.next(true))
      );
  }

  public getAdminLists() {
    return this.http
      .post<APIAdminList[]>(`${environment.apiBase}/list`, {
        method: 'list',
        data: { requestType: 'read' },
      })
      .pipe(
        mergeMap((result) =>
          this.auth.userid$.pipe(
            map((userId) =>
              result.map((list) => toAdminList(list, userId as string))
            )
          )
        )
      );
  }

  public shareList(listId: number, userId: string) {
    return this.http
      .post<unknown>(`${environment.apiBase}/listUser`, {
        method: 'listUser',
        data: { requestType: 'create', listId, userId: [userId] },
      })
      .pipe(
        catchError((e) => {
          throw new Error(e.error?.error || e.message);
        })
      );
  }

  public updateUserList(
    listId: number,
    params: Partial<{
      isFavourite: boolean;
      hasAlerts: boolean;
      configJson: unknown;
    }>
  ) {
    return this.http
      .post<unknown>(`${environment.apiBase}/listUser`, {
        method: 'listUser',
        data: { requestType: 'update', listId, ...params },
      })
      .pipe(finalize(() => this.refreshLists.next(true)));
  }

  public addReviewStatus(
    reviewedCompanyId: string,
    status: string,
    comment: string
    // log?: unknown,
    // statusLog?: unknown
  ) {
    return this.http
      .post<unknown>(`${environment.apiBase}/insertReviewStatus`, {
        method: 'insertReviewStatus',
        data: {
          reviewedCompanyId,
          status,
          comment,
        },
      })
      .pipe(
        this.changesCompanies(),
        finalize(() => this.refreshCompany.next(reviewedCompanyId))
      );
  }

  public addComment(commentedCompanyId: string, text: string) {
    return this.http.post<ApiCompanyComment>(
      `${environment.apiBase}/insertComment`,
      {
        method: 'insertComment',
        data: {
          commentedCompanyId,
          text,
        },
      }
    );
    // .pipe(finalize(() => this.refreshCompany.next(commentedCompanyId)));
  }

  public saveReviewScore(score: CompanyReviewScore) {
    return this.http
      .post<unknown>(`${environment.apiBase}/reviewScore`, {
        method: 'reviewScore',
        data: {
          requestType: score.id === null ? 'create' : 'update',
          ...(score.id === null
            ? { companyId: score.company_id, criterionId: score.criterion_id }
            : { reviewScoreId: score.id }),
          comment: score.comment || '',
          reasonForExclusion: !!score.reason_for_exclusion,
          score: score.score,
        },
      })
      .pipe(this.changesCompanies());
  }

  public asssignUser(user_id: string | null) {
    this.companyLoading.next(true);
    return this.company$.pipe(
      take(1),
      mergeMap(({ business_id, assigned_user }) =>
        this.http
          .post<unknown>(`${environment.apiBase}/api`, {
            method: 'assigneeCompany',
            data:
              user_id === null
                ? {
                    requestType: 'delete',
                    company_id: business_id,
                  }
                : {
                    requestType: assigned_user === null ? 'create' : 'update',
                    company_id: business_id,
                    user_id,
                  },
          })
          .pipe(
            this.changesCompanies(),
            finalize(() => this.reloadCompany(business_id))
          )
      )
    );
  }

  public updateOverwrites(
    entity: string,
    companyId: string,
    input: Record<string, unknown>
  ) {
    this.companyLoading.next(true);
    return this.http
      .post<ApiOverwrite>(`${environment.apiBase}/api`, {
        method: entity,
        data: { requestType: 'update', ...input },
      })
      .pipe(
        this.changesCompanies(),
        finalize(() => this.reloadCompany(companyId))
      );
  }

  /** The overwrites with these ids, with their users; without the page shade. */
  public readOverwrites(ids: string[]) {
    return this.http.post<ApiOverwrite[]>(`${environment.apiBase}/overwrite`, {
      method: 'overwrite',
      data: { requestType: 'read', id: ids },
    });
  }

  public getOverwrites<T extends { overwrite_reference: OverwriteReference }>(
    config: OverwriteConfig[],
    dataset: T
  ) {
    const { overwrite_reference, ...data } = dataset;
    this.companyLoading.next(true);
    return (
      !!overwrite_reference && !!Object.keys(overwrite_reference).length
        ? this.http.post<ApiOverwrite[]>(`${environment.apiBase}/overwrite`, {
            method: 'overwrite',
            data: {
              requestType: 'read',
              id: Object.values(overwrite_reference).filter((id) => !!id),
            },
          })
        : of([])
    ).pipe(
      map((overwrites) => toOverwriteDataset(config, overwrites, data)),
      finalize(() => this.companyLoading.next(false))
    );
  }

  /**
   * The companies of a list for the list detail page. The service keeps the
   * newest one, so the company page steps through the rows the list page
   * loaded, the way back finds them, and the lists container shows its
   * loading state. The source of the same list is reused with its rows.
   */
  public getCompanyListSource(listId: number) {
    if (listId === this.companyListId && this.companyListSource) {
      return this.companyListSource;
    }
    const source = new CompanyListSource<Company>(
      (options) => this.fetchCompanies(options, listId),
      'business_id',
      COMPANY_LIST_PAGE_SIZE,
      { cache: this.companyPages, scope: `list:${listId}`, prefetch: true }
    );
    if (this.companyListLoadingSubscription !== null) {
      this.companyListLoadingSubscription.unsubscribe();
      this.companyListLoading.next(false);
    }
    this.companyListId = listId;
    this.companyListSource = source;
    this.companyListLoadingSubscription = source.loading$.subscribe(
      (isLoading) => this.companyListLoading.next(isLoading)
    );
    return source;
  }

  /**
   * Loads one chunk of companies for the list and search tables. The request body is
   * built by `buildFetchListOfCompanyRequest`.
   */
  public fetchCompanies(
    options: CompanyListLoadOptions,
    listId: number | null = null
  ): Observable<LazyPage<Company>> {
    return this.http
      .post<{ data: ApiCompanyListItem[]; totalCount: number | null }>(
        `${environment.apiBase}/fetchListOfCompany`,
        buildFetchListOfCompanyRequest(options, listId)
      )
      .pipe(
        // A network error or a failed Lambda is repeated once. A timeout is
        // not: the same query would run into the time limit of the API again
        // and load the database twice as long. Nor is 503: a resuming
        // database is waited for by DatabaseStartingInterceptor.
        retry({
          count: 1,
          delay: (e: HttpErrorResponse) =>
            e.status === 0 ||
            (e.status >= 500 && e.status !== 503 && e.status !== 504)
              ? of(true)
              : throwError(() => e),
        }),
        catchError((e: HttpErrorResponse) => {
          throw toCompanyListError(e);
        }),
        map(({ data, totalCount }) => ({
          data: data.map(toCompanyListItem),
          totalCount,
        }))
      );
  }

  /** Company suggestions of the search box in the header. */
  public searchCompanies(needle: string): Observable<CompanyShortInfo[]> {
    return searchAutocomplete<CompanyShortInfo>(
      this.http,
      `${environment.apiBase}/autocomplete`,
      'company',
      needle
    ).pipe(map(toCompanySuggestions));
  }

  /**
   * User suggestions, named by first and last name. With a page (reviewer
   * dropdown, share sheet) up to limit + 1 users by name: one more means that
   * another page follows.
   */
  public searchUsers(
    needle: string,
    page?: AutocompletePage
  ): Observable<(User & { name: string })[]> {
    return searchAutocomplete<User & { name?: string }>(
      this.http,
      `${environment.apiBase}/autocomplete`,
      'user',
      needle,
      page
    ).pipe(
      map((users) =>
        users.map((user) => ({
          ...user,
          name:
            typeof user.name === 'string'
              ? user.name
              : user.first_name + ' ' + user.last_name,
        }))
      )
    );
  }

  /** The users of the users page, which has no API yet: none. */
  public getUsers(): Observable<User[]> {
    return of([]);
  }
}
